import React from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const RevenueMultiplierSection = ({ formData, updateFormData }) => {
  const handleMultiplierChange = (multiplier, checked) => {
    const updatedMultipliers = checked
      ? [...formData?.revenueMultipliers, multiplier]
      : formData?.revenueMultipliers?.filter(m => m !== multiplier);
    updateFormData('revenueMultipliers', updatedMultipliers);
  };

  const multipliers = [
    {
      id: 'dynamicPricing',
      label: 'Dynamic Pricing',
      description: 'Adjust rates based on demand and seasonality',
      impact: '+15-25%',
      icon: 'TrendingUp'
    },
    {
      id: 'weekendPremium',
      label: 'Weekend Premium',
      description: 'Higher rates for Friday-Sunday stays',
      impact: '+20-30%',
      icon: 'Calendar'
    },
    {
      id: 'lockSiteFees',
      label: 'Lock Site Fees',
      description: 'Additional fees for reserving specific sites',
      impact: '+5-10%',
      icon: 'Lock'
    },
    {
      id: 'eventPricing',
      label: 'Event-Based Pricing',
      description: 'Premium rates during local events/holidays',
      impact: '+30-50%',
      icon: 'Star'
    },
    {
      id: 'earlyBooking',
      label: 'Early Booking Incentives',
      description: 'Discounts for advance reservations',
      impact: '+10-15%',
      icon: 'Clock'
    },
    {
      id: 'loyaltyProgram',
      label: 'Loyalty Program',
      description: 'Repeat customer rewards and referrals',
      impact: '+8-12%',
      icon: 'Heart'
    }
  ];

  const getTotalImpact = () => {
    let totalImpact = 0;
    formData?.revenueMultipliers?.forEach(id => {
      const multiplier = multipliers?.find(m => m?.id === id);
      if (multiplier) {
        const impact = multiplier?.impact?.match(/\+(\d+)/);
        if (impact) totalImpact += parseInt(impact?.[1]);
      }
    });
    return totalImpact;
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
            <Icon name="TrendingUp" size={20} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-bold text-foreground">Revenue Multipliers</h3>
            <p className="text-sm text-muted-foreground">Select strategies to maximize your income</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm text-muted-foreground">Potential Impact</div>
          <div className="text-lg font-bold text-success">+{getTotalImpact()}%</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {multipliers?.map((multiplier) => (
          <div key={multiplier?.id} className="flex items-start space-x-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg mt-1">
              <Icon name={multiplier?.icon} size={16} color="var(--color-primary)" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <Checkbox
                  label={multiplier?.label}
                  checked={formData?.revenueMultipliers?.includes(multiplier?.id)}
                  onChange={(e) => handleMultiplierChange(multiplier?.id, e?.target?.checked)}
                />
                <span className="text-xs font-medium text-success bg-success/10 px-2 py-1 rounded">
                  {multiplier?.impact}
                </span>
              </div>
              <p className="text-xs text-muted-foreground ml-6">{multiplier?.description}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 p-4 bg-muted rounded-lg">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Info" size={16} />
          <span>
            Selected Strategies: {formData?.revenueMultipliers?.length} | These multipliers are applied to base occupancy calculations
          </span>
        </div>
      </div>
    </div>
  );
};

export default RevenueMultiplierSection;